import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, Send, Sparkles } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const GREETING =
  "Namaste! I'm Jwellary's AI stylist. Looking for a gift, a wedding piece, or a little everyday sparkle? Ask me anything.";

const CHIPS = ["Gift under ₹2,000", "Earrings for a wedding", "How do I care for it?"];

const ChatWidget = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([{ role: "assistant", content: GREETING }]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, open]);

  const send = async (text) => {
    const msg = (text || input).trim();
    if (!msg || busy) return;
    setInput("");
    const history = messages
      .filter((m) => m.content !== GREETING)
      .map((m) => ({ role: m.role, content: m.content }));
    setMessages((prev) => [...prev, { role: "user", content: msg }, { role: "assistant", content: "" }]);
    setBusy(true);
    try {
      const res = await fetch(`${API}/ai/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: msg, messages: history }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let errored = false;
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop();
        for (const part of parts) {
          const line = part.trim();
          if (!line.startsWith("data:")) continue;
          const data = JSON.parse(line.slice(5).trim());
          if (data.delta) {
            setMessages((prev) => {
              const copy = [...prev];
              copy[copy.length - 1] = { role: "assistant", content: copy[copy.length - 1].content + data.delta };
              return copy;
            });
          }
          if (data.error) errored = true;
        }
      }
      if (errored) {
        setMessages((prev) => {
          const copy = [...prev];
          copy[copy.length - 1] = { role: "assistant", content: "Sorry — my stylist signal dropped. Please try again." };
          return copy;
        });
      }
    } catch {
      setMessages((prev) => {
        const copy = [...prev];
        copy[copy.length - 1] = { role: "assistant", content: "Sorry — connection hiccup. Please try again." };
        return copy;
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.96 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="fixed bottom-[152px] right-5 md:bottom-24 z-[60] w-[calc(100vw-40px)] sm:w-[380px] h-[520px] glass gold-hairline rounded-2xl flex flex-col overflow-hidden"
            data-testid="chat-panel"
          >
            <div className="flex items-center justify-between px-5 py-4 border-b border-[#d4af37]/20 bg-[#0c0c0e]/80">
              <div className="flex items-center gap-3">
                <span className="w-9 h-9 rounded-full bg-[#d4af37]/15 border border-[#d4af37]/40 flex items-center justify-center text-[#f3e5ab]">
                  <Sparkles size={16} />
                </span>
                <div>
                  <p className="font-serif text-lg text-zinc-100 leading-none">Jwellary AI Stylist</p>
                  <p className="text-[10px] tracking-[0.18em] uppercase text-[#d4af37]/70 mt-1">Powered by ChatGPT</p>
                </div>
              </div>
              <button
                onClick={() => setOpen(false)}
                className="p-2 rounded-full text-zinc-400 hover:text-[#f3e5ab] hover:bg-[#d4af37]/10 transition-all"
                data-testid="chat-close-button"
                aria-label="Close chat"
              >
                <X size={17} strokeWidth={1.6} />
              </button>
            </div>

            <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="chat-messages">
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`} data-testid={`chat-message-${i}`}>
                  {m.content || (busy && i === messages.length - 1) ? (
                    <div
                      className={`max-w-[85%] px-4 py-2.5 text-sm leading-relaxed rounded-2xl ${
                        m.role === "user"
                          ? "bg-[#d4af37] text-[#0a0a0b] rounded-br-sm font-medium"
                          : "bg-[#18181c] text-zinc-200 border border-[#d4af37]/15 rounded-bl-sm"
                      }`}
                    >
                      {m.content ||
                        (busy && (
                          <span className="flex gap-1 py-1" data-testid="chat-typing">
                            {[0, 1, 2].map((d) => (
                              <motion.span
                                key={d}
                                className="w-1.5 h-1.5 rounded-full bg-[#d4af37]"
                                animate={{ opacity: [0.3, 1, 0.3] }}
                                transition={{ repeat: Infinity, duration: 1, delay: d * 0.2 }}
                              />
                            ))}
                          </span>
                        ))}
                    </div>
                  ) : null}
                </div>
              ))}
              {messages.length === 1 && !busy && (
                <div className="flex flex-wrap gap-2 pt-1" data-testid="chat-chips">
                  {CHIPS.map((c) => (
                    <button
                      key={c}
                      onClick={() => send(c)}
                      data-testid="chat-chip-button"
                      className="text-xs px-3.5 py-2 rounded-full border border-[#d4af37]/30 text-[#f3e5ab] hover:bg-[#d4af37]/10 hover:border-[#d4af37]/60 transition-all"
                    >
                      {c}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <form
              onSubmit={(e) => {
                e.preventDefault();
                send();
              }}
              className="p-3 border-t border-[#d4af37]/20 bg-[#0c0c0e]/80 flex gap-2"
            >
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Ask about pieces, gifting, care..."
                className="input-dark flex-1 !py-2.5"
                data-testid="chat-input"
                aria-label="Chat message"
              />
              <button
                type="submit"
                disabled={busy || !input.trim()}
                className="w-11 h-11 rounded-full bg-[#d4af37] text-[#0a0a0b] flex items-center justify-center hover:bg-[#f3e5ab] transition-colors disabled:opacity-40 disabled:pointer-events-none"
                data-testid="chat-send-button"
                aria-label="Send message"
              >
                <Send size={16} />
              </button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>

      <motion.button
        onClick={() => setOpen((v) => !v)}
        data-testid="chat-toggle-button"
        className="fixed bottom-[84px] right-5 md:bottom-5 z-[60] w-14 h-14 rounded-full bg-[#d4af37] text-[#0a0a0b] shadow-[0_10px_40px_rgba(212,175,55,0.4)] flex items-center justify-center hover:bg-[#f3e5ab] transition-colors"
        whileHover={{ scale: 1.06 }}
        whileTap={{ scale: 0.94 }}
        aria-label={open ? "Close AI stylist" : "Chat with AI stylist"}
      >
        {open ? <X size={22} strokeWidth={2} /> : <Sparkles size={22} strokeWidth={1.8} />}
      </motion.button>
    </>
  );
};

export default ChatWidget;
